<template>
  <div>
    <div class="panel-card offer-live-banner" :class="`status-${po.offerLoadedCampaignStatus}`">
      <div class="section-title section-title-row">
        <span>Oferta: {{ po.offerStatusLabel(po.offerLoadedCampaignStatus) }}</span>
        <span v-if="po.offerSyncedOk || po.offerSyncedFailed" class="chip muted" title="Financa 1 + Financa 2 OK / dështime">
          Financa {{ po.offerSyncedOk }}/{{ po.offerSyncedFailed }}
        </span>
      </div>
      <p v-if="po.offerCampaignIsReadOnly" class="empty-note">
        Kjo ofertë është e mbaruar ose e anuluar — vetëm shikim, parashikim dhe audit.
      </p>
      <p v-else-if="po.offerCampaignIsLiveEditable" class="empty-note">
        Oferta është aktive ose në pritje — ndryshimet aplikohen pas ruajtjes. Zbritja llogaritet nga çmimi bazë dhe shkruhet te kolona <strong>price</strong>.
      </p>
      <div class="result-card">
        <div class="result-item"><strong>Etiketa:</strong> {{ po.offerForm.label || "—" }}</div>
        <div class="result-item"><strong>Orari:</strong> {{ po.formatTimelinePeriod(po.offerForm.start_at, po.offerForm.end_at) }}</div>
        <div class="result-item"><strong>Rregulla:</strong> {{ po.offerRules.length }}</div>
      </div>
      <div class="quick-actions" style="margin-top: 10px" v-if="po.offerCampaignIsLiveEditable || po.canResyncOfferFailed">
        <button
          v-if="po.canApplyScheduledOfferNow"
          type="button"
          class="button is-small is-primary"
          :disabled="po.offerApplyNowLoading"
          @click="po.requestApplyScheduledOfferNow"
        >
          {{ po.offerApplyNowLoading ? "Duke aplikuar..." : "Apliko tani" }}
        </button>
        <button
          v-if="po.canCancelScheduledOffer"
          type="button"
          class="button is-small is-warning"
          :disabled="po.offerCancelScheduledLoading"
          title="Anulon ofertën në pritje pa prekur çmimet"
          @click="po.requestCancelScheduledOffer"
        >
          {{ po.offerCancelScheduledLoading ? "Duke anuluar..." : "Anulo (në pritje)" }}
        </button>
        <button
          v-if="po.offerCampaignIsLiveEditable"
          type="button"
          class="button is-small is-danger"
          @click="po.requestRollbackOfferDraft"
          :disabled="po.offerRollbackLoading"
          title="Ndalon ofertën dhe rikthen çmimet"
        >
          {{ po.offerRollbackLoading ? "Duke ndalur..." : "Ndalo ofertën" }}
        </button>
        <button
          v-if="po.canResyncOfferFailed"
          type="button"
          class="button is-small is-info"
          :disabled="po.offerResyncLoading"
          title="Riprovo sinkronizimin në Financa 1 dhe Financa 2 për produktet e dështuara"
          @click="po.requestResyncOfferFailed"
        >
          {{ po.offerResyncLoading ? "Duke riprovuar..." : `Riprovo Financa (${po.offerSyncedFailed})` }}
        </button>
      </div>
    </div>

    <div class="panel-card" v-if="po.offerCampaignIsLiveEditable">
      <div class="section-title">Ndrysho orarin dhe rregullat</div>
      <div class="field-grid two-col">
        <div>
          <label>Fillon me</label>
          <Datepicker
            :model-value="po.offerForm.start_at"
            :enable-time-picker="true"
            :is-24="true"
            auto-apply
            model-type="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm"
            @update:model-value="po.onOfferScheduleStartUpdate"
          />
        </div>
        <div>
          <label>Mbaron me</label>
          <Datepicker
            :model-value="po.offerForm.end_at"
            :enable-time-picker="true"
            :is-24="true"
            auto-apply
            model-type="yyyy-MM-dd HH:mm:ss"
            format="yyyy-MM-dd HH:mm"
            @update:model-value="po.onOfferScheduleEndUpdate"
          />
        </div>
      </div>
      <div class="manual-table-wrap" v-if="po.offerRules.length" style="margin-top: 10px">
        <table class="table is-fullwidth is-narrow">
          <thead>
            <tr>
              <th>#</th>
              <th>Ku</th>
              <th>Aplikim</th>
              <th>Vlera</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(rule, idx) in po.offerRules" :key="`live-rule-${idx}`">
              <td>{{ idx + 1 }}</td>
              <td>{{ po.offerScopeLabel(rule.scope) }}</td>
              <td>{{ po.offerTargetLabel(rule.target_field) }}</td>
              <td>{{ po.money(rule.amount) }}</td>
              <td><button type="button" class="icon-btn" @click="po.removeOfferRule(idx)"><i class="fa fa-times"></i></button></td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="compact-toolbar" style="margin-top: 10px">
        <select v-model="po.offerRuleDraft.related_entity" class="input-clean">
          <option value="products">Produkte</option>
          <option value="accessories">Aksesore</option>
        </select>
        <select v-model="po.offerRuleDraft.scope" class="input-clean">
          <option value="all">Te gjitha</option>
          <option value="tree">Nga pema</option>
          <option value="selected">Te zgjedhura</option>
        </select>
        <span class="empty-note" style="align-self: center; white-space: nowrap" title="Zbritja llogaritet nga cmimi bazë dhe aplikohet te cmimi (price)">
          Nga bazë → cmim
        </span>
        <select v-model="po.offerRuleDraft.discount_type" class="input-clean">
          <option value="percentage">Perqindje (%)</option>
          <option value="flat">Fikse (lek)</option>
          <option value="fixed_price">Cmimi i ri (lek)</option>
        </select>
        <input v-model.number="po.offerRuleDraft.amount" class="input-clean" type="number" min="0" step="0.01" />
        <button type="button" class="button is-small" @click="po.addOfferRule">+ Rregull</button>
        <button type="button" class="button is-small is-primary" @click="po.saveOfferDraft" :disabled="po.offerDraftSaving">
          {{ po.offerDraftSaving ? "Duke ruajtur..." : "Ruaj ndryshimet" }}
        </button>
      </div>
      <p class="empty-note" style="margin-top: 8px">
        Për shumë SKU, përdor import Excel/CSV në wizard (si te Çmimet).
      </p>
    </div>

    <div class="panel-card">
      <div class="section-title">Rregullat dhe parashikimi</div>
      <div class="quick-actions">
        <button type="button" class="button is-small" @click="po.simulateOfferDraft({ announce: true })" :disabled="po.offerSimulationLoading">
          {{ po.offerSimulationLoading ? "Duke simuluar..." : "Parashiko oferten" }}
        </button>
      </div>
      <OfferSimulationPreview v-if="po.offerSimulationRan" />
    </div>

    <div class="panel-card" v-if="po.offerDraftGuid && (po.offerSyncedFailed > 0 || po.offerSyncFailures.length)">
      <div class="section-title section-title-row">
        <span>Dështime Financa 1 / Financa 2</span>
        <div class="quick-actions">
          <button type="button" class="button is-small is-light" @click="po.loadOfferSyncFailures" :disabled="po.offerSyncFailuresLoading">
            Rifresko
          </button>
          <button type="button" class="button is-small is-light" @click="po.downloadOfferSyncCsv(true)">CSV dështime</button>
          <button
            v-if="po.canResyncOfferFailed"
            type="button"
            class="button is-small is-info"
            :disabled="po.offerResyncLoading"
            @click="po.requestResyncOfferFailed"
          >
            Riprovo Financa
          </button>
        </div>
      </div>
      <div v-if="po.offerSyncFailuresLoading" class="empty-note">Duke ngarkuar...</div>
      <div class="manual-table-wrap" v-else-if="po.offerSyncFailures.length">
        <table class="table is-fullwidth is-narrow">
          <thead>
            <tr>
              <th>Kodi</th>
              <th>Emri</th>
              <th>Financa</th>
              <th>Arsyeja</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in po.offerSyncFailures" :key="`sync-fail-${row.id || row.product_id}`">
              <td><code>{{ row.code || "—" }}</code></td>
              <td>{{ row.name || "—" }}</td>
              <td>{{ po.erpLabel(row.last_erp) }}</td>
              <td>{{ row.last_reason || row.last_status || "—" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty-note">Nuk ka rreshta të dështuar (numëratori mund të jetë i vjetër — Rifresko).</div>
    </div>

    <div class="panel-card" v-if="po.offerDraftGuid">
      <div class="section-title section-title-row">
        <span>Audit log</span>
        <div class="quick-actions">
          <button type="button" class="button is-small is-light" @click="po.downloadOfferSyncCsv(false)">CSV F5</button>
          <button type="button" class="button is-small is-light" @click="po.loadOfferAudits">Rifresko</button>
        </div>
      </div>
      <div class="manual-table-wrap" v-if="po.offerAudits.length">
        <table class="table is-fullwidth is-narrow audit-table">
          <thead>
            <tr>
              <th>Koha</th>
              <th>Veprimi</th>
              <th>Përdoruesi</th>
              <th>Përmbledhje</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="a in po.offerAudits" :key="`audit-${a.id}`">
              <td>{{ a.created_at }}</td>
              <td>{{ a.action }}</td>
              <td>{{ a.created_by_name || "—" }}</td>
              <td>{{ a.meta_summary || "—" }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="empty-state">Ska evente akoma.</div>
    </div>
  </div>
</template>

<script>
import Datepicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import OfferSimulationPreview from "./OfferSimulationPreview.vue";

export default {
  name: "OfertaLiveView",
  components: { Datepicker, OfferSimulationPreview },
  inject: ["po"],
};
</script>

<style scoped>
.offer-live-banner.status-active {
  border-color: #86efac;
}
.offer-live-banner.status-scheduled {
  border-color: #93c5fd;
}
.offer-live-banner.status-completed,
.offer-live-banner.status-cancelled {
  border-color: #d1d5db;
}
</style>
